import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Card, Empty, EntityChip, SortHeader, StatTile } from '../components/ui';
import { useApi } from '../lib/useApi';
import { useSort } from '../lib/useSort';

interface EndpointRow {
  id: string;
  kind: 'PUBLIC_HTTPS' | 'HOST_PORT' | 'TUNNEL' | 'INTERNAL';
  url: string | null;
  hostname: string | null;
  bindAddress: string | null;
  port: number | null;
  protocol: string | null;
  targetName: string | null;
  targetPort: number | null;
  source: string;
  isPublic: boolean;
  server: { id: string; name: string; hostname: string };
  client: { id: string; code: string; name: string } | null;
  deployment: { name: string; product: { key: string; name: string } } | null;
}

interface EndpointData {
  endpoints: EndpointRow[];
  counts: { public: number; total: number; byKind: Record<string, number> };
  note: string;
}

const KIND_LABEL: Record<EndpointRow['kind'], string> = {
  PUBLIC_HTTPS: 'Public HTTPS',
  HOST_PORT: 'Host port',
  TUNNEL: 'Cloudflare tunnel',
  INTERNAL: 'Container network',
};

/** What a person would type or click to reach it. */
function address(e: EndpointRow): string {
  if (e.url) return e.url;
  // The protocol is part of the identity, not decoration. Caddy publishes 443 on
  // both TCP and UDP (HTTP/3 is QUIC) and on both IPv4 and IPv6 — four distinct
  // bindings that render as four identical rows without it, and look like a bug.
  if (e.kind === 'HOST_PORT') return `${e.bindAddress ?? '0.0.0.0'}:${e.port}/${e.protocol ?? 'tcp'}`;
  if (e.kind === 'TUNNEL') return e.targetName ? `tunnel → ${e.targetName}:${e.targetPort ?? ''}` : 'tunnel';
  return `${e.targetName}:${e.targetPort}`;
}

export function Endpoints() {
  const { data, error, loading } = useApi<EndpointData>('/endpoints');
  const [publicOnly, setPublicOnly] = useState(true);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.endpoints ?? []).filter((e) => {
      if (publicOnly && !e.isPublic) return false;
      if (!q) return true;
      return [e.url, e.hostname, e.targetName, e.server.hostname, e.client?.name, e.deployment?.product.name]
        .filter(Boolean)
        .some((f) => f!.toLowerCase().includes(q));
    });
  }, [data, publicOnly, query]);

  // Sorted on the rendered address, so the column orders by what is on screen
  // rather than by whichever underlying field happens to be populated.
  const { sorted: rows, state: sort } = useSort<EndpointRow>(
    filtered,
    {
      address: (e) => address(e),
      kind: (e) => e.kind,
      product: (e) => e.deployment?.product.name,
      client: (e) => e.client?.code,
      server: (e) => e.server.name,
      target: (e) => e.targetName,
      source: (e) => e.source,
    },
    'address',
  );

  if (loading && !data) return <p className="empty">Loading endpoints…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;
  if (!data) return null;

  return (
    <>
      <div className="page-head">
        <h1>Endpoints</h1>
        <div className="filters">
          <input
            type="search"
            placeholder="Filter by hostname, product, server or client"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Filter endpoints"
          />
          <label className="check">
            <input type="checkbox" checked={publicOnly} onChange={(e) => setPublicOnly(e.target.checked)} />
            Reachable from outside only
          </label>
        </div>
      </div>

      <div className="tiles">
        {/* "Reachable from outside" is the number that matters, so it leads.
            A loopback binding is not exposure, and counting the two together
            would overstate the attack surface by an order of magnitude. */}
        <StatTile label="Reachable from outside" value={data.counts.public} tone={undefined} />
        <StatTile label="All endpoints" value={data.counts.total} />
        <StatTile label="Public hostnames" value={data.counts.byKind.PUBLIC_HTTPS ?? 0} sub="via Caddy" />
        <StatTile label="Tunnels" value={data.counts.byKind.TUNNEL ?? 0} sub="Cloudflare" />
      </div>

      <Card>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader label="Address" sortKey="address" state={sort} />
                <SortHeader label="Kind" sortKey="kind" state={sort} />
                <SortHeader label="Product" sortKey="product" state={sort} />
                <SortHeader label="Client" sortKey="client" state={sort} />
                <SortHeader label="Server" sortKey="server" state={sort} />
                <SortHeader label="Target" sortKey="target" state={sort} />
                <SortHeader label="Source" sortKey="source" state={sort} />
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td className="mono">
                    {e.url ? (
                      <a href={e.url} target="_blank" rel="noreferrer noopener">{e.hostname}</a>
                    ) : (
                      address(e)
                    )}
                  </td>
                  <td>
                    <EntityChip type="endpoint" label={KIND_LABEL[e.kind]} />
                    {/* Exposure is stated in words, not implied by a colour. */}
                    {e.isPublic && <span className="tag tag-warn">external</span>}
                  </td>
                  <td>
                    {e.deployment ? (
                      <EntityChip type="product" label={e.deployment.product.name} />
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {e.client ? <Link to={`/clients/${e.client.id}`}>{e.client.code}</Link> : <span className="muted">—</span>}
                  </td>
                  <td><Link to={`/servers/${e.server.id}`}>{e.server.name}</Link></td>
                  <td className="mono muted small">
                    {e.targetName ? `${e.targetName}${e.targetPort ? `:${e.targetPort}` : ''}` : '—'}
                  </td>
                  <td className="muted small">{e.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <Empty>No endpoints match.</Empty>}
      </Card>

      <p className="muted small">{data.note}</p>
    </>
  );
}
